import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, KeyVal } from 'equasion-lib';
import ZonePickerModal, {
  describeUnits,
  useZoneScanOnLoad
} from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiArrowDown, FiCheckCircle, FiEdit } from 'react-icons/fi';
import ProductInLocation from '@/lib/shared-coms/ProductInLocation';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import './MoveUnserialized.scss';
import { useRootStore } from '@/lib/providers/RootProvider';


const MoveUnserialized = ({
  moveUnserializedItems,
  getClientProductZoneInventoryBalancesCurrentZone,
  zoneInventoryBalancesCurrentZone = [],
  zoneInventoryBalancesLoadingCurrentZone = false,
  zoneInventoryBalancesErrorCurrentZone = null
}) => {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const [moveAmounts, setMoveAmounts] = useState([]);
  const [modalZoneType, setModalZoneType] = useState(null);
  const navigate = useNavigate();
  const [moved, setMoved] = useState(false);
  const [currentZoneId, setCurrentZoneId] = useState(null);
  const [currentZoneName, setCurrentZoneName] = useState('');
  const [newZoneId, setNewZoneId] = useState(null);
  const [newZoneName, setNewZoneName] = useState('');
  const isNewZoneModal = modalZoneType === 'New';
  // The screen starts at the zone the stock is leaving, so that is the scanner that opens with it.
  // The new zone opens on a tap, once the quantities are in.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setModalZoneType('Current'),
    !currentZoneId
  );
  const movingUnits = describeUnits(moveAmounts, zoneInventoryBalancesCurrentZone);

  const submitMoveUnserializedItems = async () => {
    try {
      await moveUnserializedItems({
        variables: {
          companyId,
          moveUnserializedItemsAttributes: moveAmounts
            .filter(({ quantity }) => quantity > 0)
            .map(({ id, quantity }) => ({
              zoneInventoryBalanceId: id,
              zoneId: newZoneId,
              quantity
            }))
        }
      });
      setMoved(true);
    } catch (e) {
      console.log(`ERROR UPDATING PRODUCTS: ${e}`);
    }
  };

  useEffect(() => {
    if (zoneInventoryBalancesCurrentZone) {
      setMoveAmounts(
        zoneInventoryBalancesCurrentZone.map(({ id }) => ({
          id,
          quantity: 0
        }))
      );
    }
  }, [zoneInventoryBalancesCurrentZone]);

  return (
    <Page className="move move-unserialized action-page">
      <ActionTopBar action="Move Unserialized" backPath="/" />
      <div className="action-page-body">
        <div
          className={['current-zone', ...(!currentZoneId ? ['active'] : [])].join(' ')}
          onClick={() => setModalZoneType('Current')}
        >
          <FiEdit />
          <h1>Current Zone</h1>
          <h2 className={!currentZoneName ? 'zone-placeholder' : ''}>
            {currentZoneName || tapPrompt}
          </h2>
        </div>
        {!!currentZoneId && !zoneInventoryBalancesCurrentZone.length && (
          <div className="empty-zone">Empty zone</div>
        )}
        {zoneInventoryBalancesCurrentZone.map((zoneInventoryBalance) => {
          const { id, lot, stocked, product } = zoneInventoryBalance;
          const { quantity: moveQuantity } =
            moveAmounts.find(({ id: moveId }) => id === moveId) || [];
          return (
            <ProductInLocation
              key={id}
              product={product}
              lot={lot}
              quantity={stocked}
              productId={id}
              newQuantity={moveQuantity}
              setNewQuantity={(value) =>
                setMoveAmounts(
                  moveAmounts.map((moveAmount) =>
                    id === moveAmount.id
                      ? {
                          ...moveAmount,
                          quantity: value
                            ? Math.min(parseInt(value), stocked)
                            : parseInt(value)
                        }
                      : moveAmount
                  )
                )
              }
            />
          );
        })}
        <FiArrowDown className="move-unserialized__arrow" aria-hidden />
        <div
          className={[
            'new-zone',
            ...(!!currentZoneId && !newZoneId ? ['active'] : [])
          ].join(' ')}
          onClick={() => !!currentZoneId && setModalZoneType('New')}
        >
          {!!currentZoneId && <FiEdit />}
          <h1>New Zone</h1>
          <h2 className={!newZoneName ? 'zone-placeholder' : ''}>
            {newZoneName || (currentZoneId ? tapPrompt : 'Select a Current zone first')}
          </h2>
        </div>
        <Button
          type="primary"
          fullWidth
          className="move-unserialized__submit"
          onClick={submitMoveUnserializedItems}
          disabled={!currentZoneId || !newZoneId}
        >
          Move Unserialized
        </Button>
      </div>
      {moved && (
        <div className="result-panel open">
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>Moved Unserialized Successfully</h1>
            <p>
              The following item has been moved to zone <b>{newZoneName}</b>
            </p>

            {moveAmounts
              .filter(({ quantity }) => quantity > 0)
              .map(({ id, quantity }) => ({
                ...(zoneInventoryBalancesCurrentZone.find(
                  ({ id: zoneInventoryBalanceId }) => id === zoneInventoryBalanceId
                ) || {}),
                quantity
              }))
              .map(({ id: balanceId, product, lot, quantity }) => (
                <div className="moved-products" key={balanceId}>
                  <WarehouseProductDisplay product={product} />
                  <KeyVal
                    items={[
                      {
                        key: 'Lot',
                        value: lot
                      },
                      {
                        key: 'Quantity',
                        value: quantity
                      }
                    ]}
                  />
                </div>
              ))}
          </div>
          <div className="result-actions">
            <Button type="primary" fullWidth onClick={() => navigate('/')}>
              Done
            </Button>
          </div>
        </div>
      )}
      <ZonePickerModal
        className="stock-zone-modal"
        open={!!modalZoneType}
        {...pickerProps(() => setModalZoneType(null))}
        title={`Select ${modalZoneType} zone:`}
        companyId={companyId}
        zoneTypes={['stock']}
        excludeZoneIds={isNewZoneModal && currentZoneId ? [currentZoneId] : []}
        excludeMessage={`${currentZoneName || 'That zone'} is where the stock is now — scan the zone it is moving to.`}
        scanTitle={
          isNewZoneModal ? 'Scan destination zone' : 'Scan the zone to move stock out of'
        }
        scanHint={
          isNewZoneModal
            ? `Moving ${movingUnits || 'stock'} out of ${currentZoneName}. ` +
              'Scan the label on the zone it is moving into.'
            : 'Move unserialized stock: scan the label on the zone it is moving out of. ' +
              'You choose the quantities next.'
        }
        onZoneChosen={({ id, display }) => {
          if (isNewZoneModal) {
            setNewZoneId(id);
            setNewZoneName(display);
          } else {
            setCurrentZoneId(id);
            setCurrentZoneName(display);
            getClientProductZoneInventoryBalancesCurrentZone({
              variables: { companyId, zoneId: id }
            });
          }
        }}
      />
    </Page>
  );
};

export default MoveUnserialized;
